import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Added FiStar to the destructured imports
const { FiSun, FiMoon, FiCamera, FiHeart, FiClock, FiUsers, FiDollarSign, FiChevronRight, FiStar } = FiIcons;

const Experience = ({ standalone = false }) => {
  const [selectedExperience, setSelectedExperience] = useState(0);

  const experiences = [
    {
      id: 0,
      title: 'Dawn Chorus Expedition',
      icon: FiSun,
      duration: '4 hours',
      groupSize: '2-6 people',
      price: '$75',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      description: 'Experience the magical dawn chorus as the rainforest awakens. Our expert guides will position you at strategic locations to witness peak bird activity.',
      schedule: [
        { time: '04:30', activity: 'Pre-dawn coffee and briefing' },
        { time: '05:00', activity: 'Depart for Kudawa entrance' },
        { time: '05:30', activity: 'Enter forest as first light breaks' },
        { time: '06:00-08:30', activity: 'Peak birding activity period' },
        { time: '09:00', activity: 'Return for breakfast' }
      ],
      highlights: [
        'Serendib Scops Owl territorial calls',
        'Blue Magpie morning feeding',
        'Mixed species flocks',
        'Endemic hornbill activity'
      ],
      includes: ['Expert naturalist guide', 'Field breakfast', 'Binoculars', 'Species checklist'],
      difficulty: 'Moderate'
    },
    {
      id: 1,
      title: 'Night Safari Adventure',
      icon: FiMoon,
      duration: '3 hours',
      groupSize: '2-4 people',
      price: '$95',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      description: 'Discover the nocturnal world of Sinharaja. Track owls, frogmouths, and other night birds using specialized equipment and techniques.',
      schedule: [
        { time: '18:30', activity: 'Equipment briefing and dinner' },
        { time: '19:30', activity: 'Begin night walk with red-filtered lights' },
        { time: '20:00-22:00', activity: 'Active owl and frogmouth tracking' },
        { time: '22:30', activity: 'Return with night tea service' }
      ],
      highlights: [
        'Sri Lanka Frogmouth encounters',
        'Serendib Scops Owl calls',
        'Night insect symphony',
        'Flying squirrel sightings'
      ],
      includes: ['Night vision equipment', 'Red-filtered torches', 'Hot beverages', 'Safety escort'],
      difficulty: 'Easy'
    },
    {
      id: 2,
      title: 'Photography Masterclass',
      icon: FiCamera,
      duration: '6 hours',
      groupSize: '1-3 people',
      price: '$180',
      image: 'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      description: 'Intensive photography session with professional guidance. Learn advanced techniques for capturing endemic species in challenging rainforest conditions.',
      schedule: [
        { time: '05:30', activity: 'Equipment setup and lighting workshop' },
        { time: '06:00-09:00', activity: 'Golden hour photography session' },
        { time: '09:30', activity: 'Image review and technique refinement' },
        { time: '14:00-17:00', activity: 'Afternoon hide photography' },
        { time: '17:30', activity: 'Post-processing workshop' }
      ],
      highlights: [
        'Professional hide access',
        'Advanced lighting techniques',
        'Behavioral photography tips',
        'Post-processing guidance'
      ],
      includes: ['Professional guide', 'Hide access', 'Reflectors/diffusers', 'Editing session'],
      difficulty: 'Advanced'
    },
    {
      id: 3,
      title: 'Conservation Experience',
      icon: FiHeart,
      duration: '8 hours',
      groupSize: '2-8 people',
      price: '$120',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      description: 'Participate in active conservation efforts. Learn about research projects, habitat restoration, and community engagement programs.',
      schedule: [
        { time: '07:00', activity: 'Meet with research team' },
        { time: '08:00-12:00', activity: 'Field research participation' },
        { time: '12:00', activity: 'Lunch with conservation team' },
        { time: '14:00-17:00', activity: 'Habitat restoration work' },
        { time: '17:30', activity: 'Project debrief and impact discussion' }
      ],
      highlights: [
        'Bird banding assistance',
        'Habitat mapping',
        'Community outreach',
        'Research data collection'
      ],
      includes: ['Research participation', 'Conservation materials', 'Team lunch', 'Certificate'],
      difficulty: 'Moderate'
    }
  ];

  const testimonials = [
    {
      name: 'Dr. Sarah Mitchell',
      location: 'Cambridge, UK',
      rating: 5,
      text: 'The dawn chorus expedition exceeded all expectations. Kapila\'s knowledge of bird behavior is extraordinary. We saw 23 endemics in one morning!',
      experience: 'Dawn Chorus Expedition'
    },
    {
      name: 'Marcus Chen',
      location: 'Singapore',
      rating: 5,
      text: 'As a wildlife photographer, the masterclass was invaluable. The hide access and professional guidance resulted in portfolio-quality shots.',
      experience: 'Photography Masterclass'
    },
    {
      name: 'Jennifer Walsh',
      location: 'Toronto, Canada',
      rating: 5,
      text: 'The night safari was magical. Hearing the Serendib Scops Owl call in the wild was a life-changing moment for this birder.',
      experience: 'Night Safari Adventure'
    }
  ];

  return (
    <section className={`${standalone ? 'pt-32' : 'py-20'} relative overflow-hidden`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="liquid-blob"></div>
        <div className="liquid-blob"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 glass px-6 py-3 rounded-full mb-6">
            <SafeIcon icon={FiCamera} className="w-5 h-5 text-green-400" />
            <span className="text-white font-medium">Curated Experiences</span>
          </div>
          
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white mb-6">
            Birding
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">
              Experiences
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Specially designed experiences for serious birders, photographers, and conservation enthusiasts. 
            Each program is led by expert naturalists with intimate forest knowledge.
          </p>
        </motion.div>

        {/* Experience Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
        >
          {experiences.map((exp, index) => (
            <button
              key={exp.id}
              onClick={() => setSelectedExperience(index)}
              className={`p-6 rounded-2xl text-left transition-all duration-300 ${
                selectedExperience === index
                  ? 'glass-strong ring-2 ring-green-400'
                  : 'glass hover:glass-strong'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                selectedExperience === index
                  ? 'bg-green-400'
                  : 'bg-white/20'
              }`}>
                <SafeIcon 
                  icon={exp.icon} 
                  className={`w-6 h-6 ${
                    selectedExperience === index ? 'text-white' : 'text-green-400'
                  }`} 
                />
              </div>
              <h3 className="font-bold text-white mb-2">{exp.title}</h3>
              <div className="space-y-1 text-sm text-gray-300">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiClock} className="w-3 h-3" />
                  <span>{exp.duration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiDollarSign} className="w-3 h-3" />
                  <span>{exp.price} per person</span>
                </div>
              </div>
            </button>
          ))}
        </motion.div>

        {/* Selected Experience Details */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedExperience}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="glass-strong rounded-2xl overflow-hidden mb-16"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Image */}
              <div className="relative h-80 lg:h-auto">
                <img
                  src={experiences[selectedExperience].image}
                  alt={experiences[selectedExperience].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                
                {/* Price Badge */}
                <div className="absolute top-6 right-6 glass px-4 py-2 rounded-full">
                  <span className="text-white font-bold text-lg">
                    {experiences[selectedExperience].price}
                  </span>
                  <span className="text-gray-300 text-sm"> /person</span>
                </div>

                {/* Quick Stats */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center space-x-4 text-white text-sm">
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiClock} className="w-4 h-4" />
                      <span>{experiences[selectedExperience].duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiUsers} className="w-4 h-4" />
                      <span>{experiences[selectedExperience].groupSize}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="font-display text-3xl font-bold text-white mb-4">
                  {experiences[selectedExperience].title}
                </h3>
                
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {experiences[selectedExperience].description}
                </p>

                {/* Highlights */}
                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3">Experience Highlights</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {experiences[selectedExperience].highlights.map((highlight, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-gray-300">
                        <SafeIcon icon={FiChevronRight} className="w-3 h-3 text-green-400" />
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Includes */}
                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3">Included</h4>
                  <div className="flex flex-wrap gap-2">
                    {experiences[selectedExperience].includes.map((item, index) => (
                      <span
                        key={index}
                        className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Booking */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="btn-primary px-6 py-3 flex-1">
                    Book This Experience
                  </button>
                  <button className="glass px-6 py-3 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300">
                    Ask Questions
                  </button>
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="border-t border-white/20 p-8">
              <h4 className="font-semibold text-white mb-4">Detailed Schedule</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {experiences[selectedExperience].schedule.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-medium flex-shrink-0">
                      {item.time}
                    </div>
                    <span className="text-gray-300 text-sm">{item.activity}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-16"
        >
          <h3 className="font-display text-3xl font-bold text-white text-center mb-12">
            What Our Guests Say
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass p-6 rounded-2xl"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {Array.from({ length: testimonial.rating }, (_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                
                <div>
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-gray-400 text-sm">{testimonial.location}</p>
                  <p className="text-green-400 text-sm">{testimonial.experience}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="glass-strong rounded-2xl p-8 text-center"
        >
          <h3 className="font-display text-3xl font-bold text-white mb-4">
            Ready for Your Birding Adventure?
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Our birding concierge team is available 24/7 to help you plan the perfect itinerary 
            based on your interests, skill level, and target species.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+94717714922"
              className="btn-primary px-8 py-4 text-lg"
            >
              Call Birding Concierge
            </a>
            <a
              href="https://wa.me/94717714922"
              target="_blank"
              rel="noopener noreferrer"
              className="glass px-8 py-4 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300"
            >
              WhatsApp Direct
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;